/**
 * AI Assistant Provider Component
 * Provides the AI Assistant floating button and chat panel to the application.
 */

import React, { useState, useCallback } from 'react';
import { AIAssistantButton } from './AIAssistantButton';
import { AIChatPanel } from './AIChatPanel';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isSuperUser } from '@/lib/permissions';

interface AIAssistantProviderProps {
  children: React.ReactNode;
}

export function AIAssistantProvider({ children }: AIAssistantProviderProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  // The full-page chat already is the assistant; the floating button would cover its input
  const onAIPage = useLocation().pathname.startsWith('/ai');

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Mirrors the backend AI_ASSISTANT_ALLOWED_GROUPS gate (Superuser only)
  if (!isSuperUser(user?.groups) || onAIPage) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <AIAssistantButton onClick={handleOpen} isOpen={isOpen} />
      <AIChatPanel isOpen={isOpen} onClose={handleClose} />
    </>
  );
}
